/**
 * Sign-in, plus first-run setup.
 *
 * A brand-new deployment has no accounts, and the backend lets the *first*
 * registration through unauthenticated (forced to admin). This page offers
 * that path explicitly so a fresh install is not a dead end.
 */

import { useState, type FormEvent } from 'react';

import * as api from '../lib/api';
import { useSession } from '../lib/session';

export default function LoginPage() {
  const { signIn } = useSession();
  const [mode, setMode] = useState<'signin' | 'setup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'setup') {
        await api.registerFirstAdmin(email, password, name);
        // Registering the bootstrap admin returns a session, but going
        // through signIn keeps a single path for populating session state.
        await signIn(email, password);
      } else {
        await signIn(email, password);
      }
    } catch (err) {
      const apiError = err as api.ApiError;
      setError(
        apiError.isOffline
          ? 'Cannot reach the server. Check your connection and try again.'
          : apiError.message,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="centered">
      <form className="card auth-card" onSubmit={handleSubmit}>
        <h1>Convention POS</h1>
        <p className="muted">
          {mode === 'signin'
            ? 'Sign in to continue.'
            : 'Create the first admin account for this deployment.'}
        </p>

        {mode === 'setup' && (
          <label>
            Name
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoComplete="name"
            />
          </label>
        )}

        <label>
          Email
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="username"
            required
          />
        </label>

        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete={mode === 'setup' ? 'new-password' : 'current-password'}
            required
            minLength={8}
          />
        </label>

        {error && <p className="error">{error}</p>}

        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'Working…' : mode === 'signin' ? 'Sign in' : 'Create admin account'}
        </button>

        <button
          type="button"
          className="ghost"
          onClick={() => {
            setMode(mode === 'signin' ? 'setup' : 'signin');
            setError('');
          }}
        >
          {mode === 'signin'
            ? 'First time here? Set up the first admin account'
            : 'Back to sign in'}
        </button>
      </form>
    </div>
  );
}
